import type { IPageAuthor } from "@/types/author";

/**
 * Shared helpers for the page-author widgets — Author Card (3003) and Author
 * Byline (3004). Both render ONLY on a blog page that has an author linked, and
 * both take every piece of content from the page payload rather than their own
 * config, so the read/format rules live here instead of being duplicated twice.
 */

/**
 * Local copy of PAGE_TYPES.BLOG. Importing webapp/constants.js from a widget would
 * pull in the module that lazily imports every widget — this avoids that cycle.
 */
export const BLOG_PAGE_TYPE = "blog";

export const isBlogPageType = (pageCategory: unknown): boolean =>
  String(pageCategory ?? "")
    .trim()
    .toLowerCase() === BLOG_PAGE_TYPE;

/**
 * Author profile link. `author.slug` is the PATH of the page that renders the
 * author's profile and is optional — with none set we deliberately fall back to
 * "#" so the name stays visually a link but goes nowhere.
 */
export const resolveAuthorProfileHref = (
  author?: Pick<IPageAuthor, "slug"> | null,
): string => {
  const slug = String(author?.slug ?? "").trim();
  if (!slug) return "#";
  if (/^(https?:)?\/\//i.test(slug) || slug.startsWith("/") || slug.startsWith("#")) {
    return slug;
  }
  return `/${slug}`;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * "Jul 21, 2026". Formatted from UTC parts rather than toLocaleDateString so the
 * server and the browser always produce the same string (a locale/timezone split
 * would otherwise surface as a hydration mismatch).
 */
export const formatUpdatedOn = (raw?: string | number | Date | null): string => {
  if (raw == null || raw === "") return "";
  const d = new Date(raw as string);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};

const trimTrailingZeros = (s: string) =>
  s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;

/** Raw view count → the abbreviated form in the design: 951840 → "951.84K+". */
export const formatCompactViews = (raw?: number | string | null): string => {
  if (raw == null || raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n >= 1_000_000) return `${trimTrailingZeros((n / 1_000_000).toFixed(2))}M+`;
  if (n >= 1_000) return `${trimTrailingZeros((n / 1_000).toFixed(2))}K+`;
  return String(Math.round(n));
};

/** "426 articles published" / "1 article published". Empty when the count is unknown. */
export const formatArticlesPublished = (
  count?: number | string | null,
): string => {
  if (count == null || count === "") return "";
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return "";
  return `${n} article${n === 1 ? "" : "s"} published`;
};

/**
 * Stand-in used ONLY by the CMS widget preview, where no page (and therefore no
 * linked author) exists. Live pages never reach this — they render nothing at all
 * unless a real author is linked.
 */
export const PREVIEW_AUTHOR: IPageAuthor = {
  id: 0,
  name: "Author Name",
  slug: null,
  description:
    "The linked author's bio is shown here. Every detail in this widget — photo, name, article count and bio — comes from the author linked to the blog page.",
  profile_image_url: null,
  articles_published: 426,
};

/** Page meta the Author Byline renders; supplied by NewPage from the page payload. */
export interface IAuthorPageMeta {
  /** Editor-set "Updated on" date (YYYY-MM-DD). Wins over the timestamps below. */
  custom_blog_updated_date?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  read_time?: number | string | null;
  views?: number | string | null;
}

/**
 * "Updated on Jul 21, 2026 | 10 min read | 951.84K+ views".
 *
 * The date prefers the editor-set `custom_blog_updated_date`, falling back to the
 * page's own updated_at (then created_at) — those move on every edit, so they are
 * the fallback rather than the source of truth.
 *
 * Read time and views are optional page fields — when unset their segment is
 * dropped rather than faked.
 */
export const buildBylineMetaLine = (meta?: IAuthorPageMeta | null): string => {
  const segments: string[] = [];

  const updatedOn = formatUpdatedOn(
    meta?.custom_blog_updated_date ?? meta?.updated_at ?? meta?.created_at ?? null,
  );
  if (updatedOn) segments.push(`Updated on ${updatedOn}`);

  const minutes = Number(meta?.read_time);
  if (Number.isFinite(minutes) && minutes > 0) {
    segments.push(`${Math.round(minutes)} min read`);
  }

  const views = formatCompactViews(meta?.views);
  if (views) segments.push(`${views} views`);

  return segments.join(" | ");
};
